import ProductImages from '@/components/products/ProductImages'
import { ProductVariantsSection } from '@/components/products/ProductVariantsSection'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { Product as ApiProduct, Category, CreateProductData, FlowRateUnit } from '@/types/api'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  ArrowLeft,
  DollarSign,
  Package,
  RefreshCw,
  RotateCcw,
  Save,
  Star,
  StarOff,
  Wrench,
  Zap,
} from 'lucide-react'
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'

const productDetailSchema = z.object({
  name: z.string().min(1, 'Product name is required'),
  sku: z.string().min(1, 'SKU is required'),
  slug: z.string().optional(),
  description: z.string().min(1, 'Description is required'),
  short_description: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  specifications: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  unit: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  type: z.enum(['water-pumps', 'spare-parts', 'accessories']).nullable().optional(),
  category_id: z.string().min(1, 'Category is required'),
  brand: z.string().optional(),
  price: z.number().min(0, 'Price must be positive'),
  stock_quantity: z.number().min(0, 'Stock must be positive'),
  production_status: z.enum(['ready', 'pre_order', 'discontinued']),
  is_active: z.boolean(),
  is_featured: z.boolean(),
  lead_time: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  warranty: z.number().min(0, 'Warranty must be a positive number'),
  // Technical specs
  flow_rate: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  flow_rate_unit: z.enum(['m3_h', 'l_min']).default('m3_h'),
  head: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  suction_lift_max: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  pressure_max: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  fluid_temperature: z
    .union([z.number(), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  ambient_temperature: z
    .union([z.number(), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  motor_power: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  voltage_phase: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  rpm: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  motor_efficiency: z
    .union([z.number().min(0).max(100), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  isolation_class: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  ip_protection: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  // Materials
  housing_material: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  impeller_material: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  shaft_material: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  mechanical_seal: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  inlet_outlet_connection: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  product_weight: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  shipping_weight: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  product_dimensions: z
    .union([z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => (val === '' || val === null || val === undefined ? undefined : val)),
  length: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  width: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  height: z
    .union([z.number().min(0), z.string(), z.null(), z.undefined()])
    .optional()
    .transform((val) => {
      if (val === '' || val === null || val === undefined) return undefined
      const num = typeof val === 'string' ? parseFloat(val) : val
      return isNaN(num) ? undefined : num
    }),
  // Additional
  country_of_origin: z.string().optional(),
  use_case: z.string().optional(),
  additional_features: z.string().optional(),
  internal_notes: z.string().optional(),
})

type ProductDetailFormData = z.infer<typeof productDetailSchema>

const DEFAULT_FLOW_RATE_UNIT: FlowRateUnit = 'm3_h'
const LITERS_PER_MINUTE_IN_CUBIC_METER_PER_HOUR = 16.6666666667

const roundFlowRateValue = (value: number) => Math.round(value * 100) / 100

const normalizeFlowRateUnitValue = (unit?: string | null): FlowRateUnit => {
  return unit === 'l_min' ? 'l_min' : DEFAULT_FLOW_RATE_UNIT
}

const convertFlowRateToCanonical = (value: number, unit: FlowRateUnit) => {
  if (unit === 'l_min') {
    return roundFlowRateValue(value / LITERS_PER_MINUTE_IN_CUBIC_METER_PER_HOUR)
  }

  return roundFlowRateValue(value)
}

const convertFlowRateFromCanonical = (value: number, unit: FlowRateUnit) => {
  if (unit === 'l_min') {
    return roundFlowRateValue(value * LITERS_PER_MINUTE_IN_CUBIC_METER_PER_HOUR)
  }

  return roundFlowRateValue(value)
}

const formatRupiah = (value: number) => {
  return new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(value)
}

const parseRupiahToNumber = (value: string) => {
  const digits = value.replace(/\D/g, '')
  return digits ? Number(digits) : 0
}

const parseAdditionalFeatures = (value: string | undefined): string[] => {
  if (!value) return []
  return value
    .split('\n')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
}

const additionalFeaturesToText = (value: string[] | undefined): string => {
  if (!value || value.length === 0) return ''
  return value.join('\n')
}

const normalizeProductionStatusValue = (
  status?: string | null
): 'ready' | 'pre_order' | 'discontinued' => {
  switch (status) {
    case 'pre_order':
      return 'pre_order'
    case 'discontinued':
      return 'discontinued'
    case 'ready':
    default:
      return 'ready'
  }
}

const getProductStatusLabel = (status?: string | null) => {
  switch (normalizeProductionStatusValue(status)) {
    case 'pre_order':
      return 'Pre-Order'
    case 'discontinued':
      return 'Discontinued'
    case 'ready':
    default:
      return 'Ready'
  }
}

const getProductStatusBadgeClassName = (status?: string | null) => {
  switch (normalizeProductionStatusValue(status)) {
    case 'pre_order':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    case 'discontinued':
      return 'border-red-200 bg-red-50 text-red-700'
    case 'ready':
    default:
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  }
}

const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphen
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
}

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [categories, setCategories] = useState<Category[]>([])
  const [product, setProduct] = useState<ApiProduct | null>(null)
  const [relatedProducts, setRelatedProducts] = useState<ApiProduct[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const [uploadingVariantId, setUploadingVariantId] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [priceDisplay, setPriceDisplay] = useState('')

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
    setFocus,
  } = useForm<ProductDetailFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(productDetailSchema) as any,
    mode: 'onChange',
  })

  const watchedValues = watch()

  useEffect(() => {
    setHasChanges(isDirty)
  }, [isDirty, watchedValues])

  const loadProduct = useCallback(async () => {
    if (!id) return
    setIsLoading(true)
    try {
      const response = await apiClient.getProduct(id)
      const prod = response.data
      setProduct(prod)
      const flowRateUnit = normalizeFlowRateUnitValue(prod.flow_rate_unit)

      // Populate form
      reset({
        name: prod.name || '',
        sku: prod.sku || '',
        slug: prod.slug || '',
        description: prod.description || '',
        short_description: prod.short_description || '',
        specifications: typeof prod.specifications === 'string' ? prod.specifications : '',
        unit: prod.unit || '',
        type: prod.type ?? null,
        category_id: prod.category_id ? String(prod.category_id) : '',
        brand: prod.brand || '',
        price: prod.price || 0,
        stock_quantity: prod.stock_quantity || 0,
        production_status: normalizeProductionStatusValue(prod.production_status),
        is_active: prod.is_active ?? true,
        is_featured: prod.is_featured ?? false,
        lead_time: prod.lead_time ?? undefined,
        warranty: prod.warranty ?? undefined,
        flow_rate:
          prod.flow_rate == null
            ? undefined
            : convertFlowRateFromCanonical(prod.flow_rate, flowRateUnit),
        flow_rate_unit: flowRateUnit,
        head: prod.head ?? undefined,
        suction_lift_max: prod.suction_lift_max ?? undefined,
        pressure_max: prod.pressure_max ?? undefined,
        fluid_temperature: prod.fluid_temperature ?? undefined,
        ambient_temperature: prod.ambient_temperature ?? undefined,
        motor_power: prod.motor_power ?? undefined,
        voltage_phase: prod.voltage_phase || '',
        rpm: prod.rpm ?? undefined,
        motor_efficiency: prod.motor_efficiency ?? undefined,
        isolation_class: prod.isolation_class || '',
        ip_protection: prod.ip_protection || '',
        housing_material: prod.housing_material || '',
        impeller_material: prod.impeller_material || '',
        shaft_material: prod.shaft_material || '',
        mechanical_seal: prod.mechanical_seal || '',
        inlet_outlet_connection: prod.inlet_outlet_connection || '',
        product_weight: prod.product_weight ?? undefined,
        shipping_weight: prod.shipping_weight ?? undefined,
        product_dimensions: prod.product_dimensions || '',
        length: prod.length ?? undefined,
        width: prod.width ?? undefined,
        height: prod.height ?? undefined,
        country_of_origin: prod.country_of_origin || '',
        use_case: prod.use_case || '',
        additional_features: additionalFeaturesToText(prod.additional_features),
        internal_notes: prod.internal_notes || '',
      })
      setPriceDisplay(formatRupiah(prod.price || 0))
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load product. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }, [id, reset, toast])

  const loadRelated = useCallback(async () => {
    if (!id) return
    try {
      const response = await apiClient.getRelatedProducts(id, 8)
      setRelatedProducts(response.data)
    } catch {
      // Silently fail for related products
    }
  }, [id])

  const loadCategories = useCallback(async () => {
    try {
      const response = await apiClient.getCategories({ per_page: 100 })
      setCategories(response.data || [])
    } catch {
      toast({
        title: 'Warning',
        description: 'Failed to load categories. Please refresh the page.',
        variant: 'destructive',
      })
    }
  }, [toast])

  useEffect(() => {
    loadProduct()
    loadRelated()
    loadCategories()
  }, [loadProduct, loadRelated, loadCategories])

  useEffect(() => {
    if (product && categories.length > 0 && product.category_id) {
      const categoryId = String(product.category_id)
      const categoryExists = categories.some((cat) => String(cat.id) === categoryId)
      if (categoryExists) {
        setValue('category_id', categoryId)
      }
    }
  }, [product, categories, setValue])

  const onSubmit = async (data: ProductDetailFormData) => {
    if (!id) return
    setIsSaving(true)
    try {
      const normalizedFlowRate =
        data.flow_rate === undefined || data.flow_rate === null || isNaN(data.flow_rate)
          ? null
          : convertFlowRateToCanonical(data.flow_rate, data.flow_rate_unit)

      const submitData = {
        ...data,
        short_description:
          data.short_description === undefined ||
          data.short_description === null ||
          data.short_description === ''
            ? null
            : data.short_description,
        specifications:
          data.specifications === undefined ||
          data.specifications === null ||
          data.specifications === ''
            ? null
            : data.specifications,
        unit: data.unit === undefined || data.unit === null || data.unit === '' ? null : data.unit,
        type: data.type === undefined || data.type === null ? null : data.type,
        lead_time:
          data.lead_time === undefined || data.lead_time === null || isNaN(data.lead_time)
            ? null
            : data.lead_time,
        flow_rate: normalizedFlowRate,
        flow_rate_unit: data.flow_rate_unit,
        head: data.head === undefined || data.head === null || isNaN(data.head) ? null : data.head,
        suction_lift_max:
          data.suction_lift_max === undefined ||
          data.suction_lift_max === null ||
          isNaN(data.suction_lift_max)
            ? null
            : data.suction_lift_max,
        pressure_max:
          data.pressure_max === undefined || data.pressure_max === null || isNaN(data.pressure_max)
            ? null
            : data.pressure_max,
        fluid_temperature:
          data.fluid_temperature === undefined ||
          data.fluid_temperature === null ||
          isNaN(data.fluid_temperature)
            ? null
            : data.fluid_temperature,
        ambient_temperature:
          data.ambient_temperature === undefined ||
          data.ambient_temperature === null ||
          isNaN(data.ambient_temperature)
            ? null
            : data.ambient_temperature,
        motor_power:
          data.motor_power === undefined || data.motor_power === null || isNaN(data.motor_power)
            ? null
            : data.motor_power,
        voltage_phase:
          data.voltage_phase === undefined ||
          data.voltage_phase === null ||
          data.voltage_phase === ''
            ? null
            : data.voltage_phase,
        rpm: data.rpm === undefined || data.rpm === null || isNaN(data.rpm) ? null : data.rpm,
        motor_efficiency:
          data.motor_efficiency === undefined ||
          data.motor_efficiency === null ||
          isNaN(data.motor_efficiency)
            ? null
            : data.motor_efficiency,
        isolation_class:
          data.isolation_class === undefined ||
          data.isolation_class === null ||
          data.isolation_class === ''
            ? null
            : data.isolation_class,
        ip_protection:
          data.ip_protection === undefined ||
          data.ip_protection === null ||
          data.ip_protection === ''
            ? null
            : data.ip_protection,
        housing_material:
          data.housing_material === undefined ||
          data.housing_material === null ||
          data.housing_material === ''
            ? null
            : data.housing_material,
        impeller_material:
          data.impeller_material === undefined ||
          data.impeller_material === null ||
          data.impeller_material === ''
            ? null
            : data.impeller_material,
        shaft_material:
          data.shaft_material === undefined ||
          data.shaft_material === null ||
          data.shaft_material === ''
            ? null
            : data.shaft_material,
        mechanical_seal:
          data.mechanical_seal === undefined ||
          data.mechanical_seal === null ||
          data.mechanical_seal === ''
            ? null
            : data.mechanical_seal,
        inlet_outlet_connection:
          data.inlet_outlet_connection === undefined ||
          data.inlet_outlet_connection === null ||
          data.inlet_outlet_connection === ''
            ? null
            : data.inlet_outlet_connection,
        product_weight:
          data.product_weight === undefined ||
          data.product_weight === null ||
          isNaN(data.product_weight)
            ? null
            : data.product_weight,
        shipping_weight:
          data.shipping_weight === undefined ||
          data.shipping_weight === null ||
          isNaN(data.shipping_weight)
            ? null
            : data.shipping_weight,
        product_dimensions:
          data.product_dimensions === undefined ||
          data.product_dimensions === null ||
          data.product_dimensions === ''
            ? null
            : data.product_dimensions,
        length:
          data.length === undefined || data.length === null || isNaN(data.length)
            ? null
            : data.length,
        width:
          data.width === undefined || data.width === null || isNaN(data.width) ? null : data.width,
        height:
          data.height === undefined || data.height === null || isNaN(data.height)
            ? null
            : data.height,
        additional_features: parseAdditionalFeatures(data.additional_features),
      }
      await apiClient.updateProduct(id, submitData as unknown as CreateProductData)
      toast({
        title: 'Success',
        description: 'Product updated successfully',
      })
      // Reload product to get latest data
      await loadProduct()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update product. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const onFormError = () => {
    // Focus on the first invalid field
    if (errors.name) {
      setFocus('name')
    } else if (errors.sku) {
      setFocus('sku')
    } else if (errors.description) {
      setFocus('description')
    } else if (errors.category_id) {
      setFocus('category_id')
    } else if (errors.price) {
      setFocus('price')
    } else if (errors.stock_quantity) {
      setFocus('stock_quantity')
    }
  }

  const handleReset = () => {
    if (product) {
      const flowRateUnit = normalizeFlowRateUnitValue(product.flow_rate_unit)

      reset({
        name: product.name || '',
        sku: product.sku || '',
        slug: product.slug || '',
        description: product.description || '',
        short_description: product.short_description || '',
        specifications: typeof product.specifications === 'string' ? product.specifications : '',
        unit: product.unit || '',
        type: product.type ?? null,
        category_id: product.category_id ? String(product.category_id) : '',
        brand: product.brand || '',
        price: product.price || 0,
        stock_quantity: product.stock_quantity || 0,
        production_status: normalizeProductionStatusValue(product.production_status),
        is_active: product.is_active ?? true,
        is_featured: product.is_featured ?? false,
        lead_time: product.lead_time ?? undefined,
        warranty: product.warranty ?? undefined,
        flow_rate:
          product.flow_rate == null
            ? undefined
            : convertFlowRateFromCanonical(product.flow_rate, flowRateUnit),
        flow_rate_unit: flowRateUnit,
        head: product.head ?? undefined,
        suction_lift_max: product.suction_lift_max ?? undefined,
        pressure_max: product.pressure_max ?? undefined,
        fluid_temperature: product.fluid_temperature ?? undefined,
        ambient_temperature: product.ambient_temperature ?? undefined,
        motor_power: product.motor_power ?? undefined,
        voltage_phase: product.voltage_phase || '',
        rpm: product.rpm ?? undefined,
        motor_efficiency: product.motor_efficiency ?? undefined,
        isolation_class: product.isolation_class || '',
        ip_protection: product.ip_protection || '',
        housing_material: product.housing_material || '',
        impeller_material: product.impeller_material || '',
        shaft_material: product.shaft_material || '',
        mechanical_seal: product.mechanical_seal || '',
        inlet_outlet_connection: product.inlet_outlet_connection || '',
        product_weight: product.product_weight ?? undefined,
        shipping_weight: product.shipping_weight ?? undefined,
        product_dimensions: product.product_dimensions || '',
        length: product.length ?? undefined,
        width: product.width ?? undefined,
        height: product.height ?? undefined,
        country_of_origin: product.country_of_origin || '',
        use_case: product.use_case || '',
        additional_features: additionalFeaturesToText(product.additional_features),
        internal_notes: product.internal_notes || '',
      })
    }
  }

  const handleFlowRateUnitChange = (nextUnit: FlowRateUnit) => {
    const currentUnit = watchedValues.flow_rate_unit ?? DEFAULT_FLOW_RATE_UNIT
    const currentFlowRate = watchedValues.flow_rate

    if (currentFlowRate !== undefined && currentFlowRate !== null && !isNaN(currentFlowRate)) {
      const canonicalFlowRate = convertFlowRateToCanonical(currentFlowRate, currentUnit)
      const convertedFlowRate = convertFlowRateFromCanonical(canonicalFlowRate, nextUnit)

      setValue('flow_rate', convertedFlowRate, { shouldDirty: true, shouldValidate: true })
    }

    setValue('flow_rate_unit', nextUnit, { shouldDirty: true, shouldValidate: true })
  }

  const handleTogglePublish = async () => {
    if (!product || !id) return
    try {
      await apiClient.toggleProductPublish(id, !product.is_active)
      toast({
        title: 'Success',
        description: 'Product status updated',
      })
      await loadProduct()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update product status.',
        variant: 'destructive',
      })
    }
  }

  const handleToggleFeatured = async () => {
    if (!product || !id) return
    try {
      await apiClient.toggleProductFeatured(id, !product.is_featured)
      toast({
        title: 'Success',
        description: 'Product featured status updated',
      })
      await loadProduct()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update featured status.',
        variant: 'destructive',
      })
    }
  }

  const handleImageUpload = async (files: FileList | null) => {
    if (!id || !files || files.length === 0) return

    const file = files[0]
    setIsUploadingImage(true)

    try {
      await apiClient.uploadProductImage(id, file)
      await loadProduct()
      toast({
        title: 'Success',
        description: 'Image uploaded successfully',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to upload image. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsUploadingImage(false)
    }
  }

  const handleDeleteImage = async (imageId: string) => {
    if (!id || !product) return
    setIsUploadingImage(true)
    try {
      await apiClient.deleteProductImage(id, imageId)
      toast({
        title: 'Success',
        description: 'Image removed',
      })
      await loadProduct()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to remove image.',
        variant: 'destructive',
      })
    } finally {
      setIsUploadingImage(false)
    }
  }

  const handleSetPrimaryImage = async (imageId: string) => {
    if (!id || !product) return
    setIsUploadingImage(true)
    try {
      await apiClient.setPrimaryProductImage(id, imageId)
      await loadProduct()
      toast({
        title: 'Success',
        description: 'Primary image set successfully',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to set primary image. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsUploadingImage(false)
    }
  }

  const handleVariantImageUpload = async (variantId: string, files: FileList | null) => {
    if (!id || !files || files.length === 0) return

    setUploadingVariantId(variantId)

    try {
      await apiClient.uploadProductVariantImage(id, variantId, files[0])
      await loadProduct()
      toast({
        title: 'Success',
        description: 'Variant image uploaded successfully',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to upload variant image. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setUploadingVariantId(null)
    }
  }

  const handleDeleteVariantImage = async (variantId: string, imageId: string) => {
    if (!id) return

    setUploadingVariantId(variantId)

    try {
      await apiClient.deleteProductVariantImage(id, variantId, imageId)
      toast({
        title: 'Success',
        description: 'Variant image removed',
      })
      await loadProduct()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to remove variant image.',
        variant: 'destructive',
      })
    } finally {
      setUploadingVariantId(null)
    }
  }

  const handleSetPrimaryVariantImage = async (variantId: string, imageId: string) => {
    if (!id) return

    setUploadingVariantId(variantId)

    try {
      await apiClient.setPrimaryProductVariantImage(id, variantId, imageId)
      await loadProduct()
      toast({
        title: 'Success',
        description: 'Primary variant image set successfully',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to set primary variant image. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setUploadingVariantId(null)
    }
  }

  const handleGenerateSlug = async () => {
    const productName = watchedValues.name
    if (!productName) return

    let slug = generateSlug(productName)
    let isUnique = false
    let counter = 1
    const originalSlug = slug

    // Check if slug is unique, if not, append number
    while (!isUnique) {
      try {
        const response = await apiClient.checkSlugAvailability(slug, id)
        if (response.data.available) {
          isUnique = true
        } else {
          // If slug exists, append counter
          slug = `${originalSlug}-${counter}`
          counter++
        }
      } catch (error) {
        // If error checking slug, just use the generated slug
        console.error('Error checking slug availability:', error)
        isUnique = true
      }
    }

    setValue('slug', slug)
    toast({
      title: 'Success',
      description: 'Slug generated successfully',
    })
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price)
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Product not found.</p>
        <Button className="mt-4" onClick={() => navigate('/products')}>
          Back to Products
        </Button>
      </div>
    )
  }

  const hasVariants = Boolean(product.has_variants && (product.variants?.length ?? 0) > 0)
  const sortedVariants = [...(product.variants ?? [])].sort(
    (a, b) => Number(b.is_default) - Number(a.is_default) || a.sort_order - b.sort_order
  )

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/products')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold">{product.name}</h1>
            <p className="text-muted-foreground">SKU: {product.sku}</p>
            <p className="text-sm text-muted-foreground">
              Rating: {product.rating_average?.toFixed(2) ?? '0.00'} ({product.review_count ?? 0}{' '}
              reviews)
            </p>
            {hasVariants && (
              <p className="text-sm font-medium text-blue-700">
                Product ini memakai variants. Harga dan stok parent menjadi summary dari variant
                aktif.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-start gap-3 md:justify-end">
            <div className="flex flex-wrap items-center gap-2 rounded-full border border-border bg-background px-2 py-1 shadow-sm">
              <span className="px-2 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Status
              </span>
              <Badge
                variant="outline"
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  product.is_active
                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : 'border-slate-200 bg-slate-50 text-slate-600'
                }`}
              >
                {product.is_active ? 'Active' : 'Inactive'}
              </Badge>
              <Badge
                variant="outline"
                className={`rounded-full px-3 py-1 text-xs font-semibold ${getProductStatusBadgeClassName(
                  product.production_status
                )}`}
              >
                {getProductStatusLabel(product.production_status)}
              </Badge>
            </div>

            <div className="flex flex-wrap items-center gap-2 rounded-full border border-border bg-background px-2 py-1 shadow-sm">
              <span className="px-2 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Actions
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleToggleFeatured}
                className={`h-8 rounded-full px-3 text-sm font-medium ${
                  product.is_featured
                    ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {product.is_featured ? (
                  <Star className="mr-1.5 h-4 w-4" />
                ) : (
                  <StarOff className="mr-1.5 h-4 w-4" />
                )}
                {product.is_featured ? 'Featured' : 'Mark Featured'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleTogglePublish}
                className={`h-8 rounded-full px-3 text-sm font-medium ${
                  product.is_active
                    ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                    : 'text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800'
                }`}
              >
                {product.is_active ? 'Deactivate' : 'Activate'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
        {/* Left column: form sections */}
        <form onSubmit={handleSubmit(onSubmit, onFormError)} className="mt-0 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Package className="h-5 w-5 mr-2" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                id="name"
                label="Product Name"
                error={!!errors.name}
                required
                showError={false}
              >
                <Input className="bg-muted/60" {...register('name')} />
              </FormField>
              <FormField id="sku" label="SKU" error={!!errors.sku} required showError={false}>
                <Input className="bg-muted/60" {...register('sku')} />
              </FormField>
              <div>
                <Label htmlFor="slug">Slug</Label>
                <div className="relative">
                  <Input id="slug" className="bg-muted/60 pr-20" {...register('slug')} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleGenerateSlug}
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-muted"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <FormField
                id="category_id"
                label="Category"
                error={!!errors.category_id}
                required
                showError={false}
              >
                <div className="relative">
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-muted/60 px-3 py-2 pr-10 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer"
                    {...register('category_id')}
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={String(cat.id)}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="fill-current h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormField>
              <div>
                <Label htmlFor="type">Product Type</Label>
                <div className="relative">
                  <select
                    id="type"
                    {...register('type', {
                      setValueAs: (value) =>
                        value === '' || value === null || value === undefined ? null : value,
                    })}
                    className="flex h-10 w-full rounded-md border border-input bg-muted/60 px-3 py-2 pr-10 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer"
                  >
                    <option value="">Select type</option>
                    <option value="water-pumps">Water Pumps</option>
                    <option value="spare-parts">Spare Parts</option>
                    <option value="accessories">Accessories</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="fill-current h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="unit">Unit</Label>
                <Input id="unit" className="bg-muted/60" {...register('unit')} />
              </div>
              <div>
                <Label htmlFor="brand">Brand</Label>
                <Input id="brand" className="bg-muted/60" {...register('brand')} />
              </div>
              <div>
                <Label htmlFor="country_of_origin">Country of Origin</Label>
                <Input
                  id="country_of_origin"
                  className="bg-muted/60"
                  {...register('country_of_origin')}
                />
              </div>
              <FormField
                id="description"
                label="Description"
                error={!!errors.description}
                required
                showError={false}
                className="md:col-span-2"
              >
                <Textarea rows={8} className="bg-muted/60" {...register('description')} />
              </FormField>
              <div className="md:col-span-2">
                <Label htmlFor="short_description">Short Description</Label>
                <Textarea
                  id="short_description"
                  className="bg-muted/60"
                  {...register('short_description')}
                  rows={3}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="specifications">Specifications</Label>
                <Textarea
                  id="specifications"
                  className="bg-muted/60"
                  {...register('specifications')}
                  rows={6}
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="additional_features">Additional Features (one per line)</Label>
                <Textarea
                  id="additional_features"
                  className="bg-muted/60"
                  {...register('additional_features')}
                  rows={4}
                />
              </div>
              <div>
                <Label htmlFor="use_case">Use Case</Label>
                <Textarea
                  id="use_case"
                  className="bg-muted/60"
                  {...register('use_case')}
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="internal_notes">Internal Notes</Label>
                <Textarea
                  id="internal_notes"
                  {...register('internal_notes')}
                  rows={2}
                  className="bg-muted/60"
                />
              </div>
            </CardContent>
          </Card>

          {/* Pricing & Inventory */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <DollarSign className="h-5 w-5 mr-2" />
                Pricing & Inventory
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField id="price" label="Price" error={!!errors.price} required showError={false}>
                <Controller
                  control={control}
                  name="price"
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className="bg-muted/60"
                      disabled={hasVariants}
                      value={priceDisplay}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const numeric = parseRupiahToNumber(event.target.value)
                        field.onChange(numeric)
                        setPriceDisplay(formatRupiah(numeric))
                      }}
                    />
                  )}
                />
              </FormField>
              <FormField
                id="stock_quantity"
                label="Stock Quantity"
                error={!!errors.stock_quantity}
                required
                showError={false}
              >
                <Input
                  type="number"
                  className="bg-muted/60"
                  disabled={hasVariants}
                  {...register('stock_quantity', { valueAsNumber: true })}
                />
              </FormField>
              {hasVariants && (
                <div className="md:col-span-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                  Nilai price dan stock parent dikunci saat variant aktif. Ubah nilainya dari
                  section <strong>Product Variants</strong> di bawah.
                </div>
              )}
              <div>
                <Label htmlFor="production_status">Product Status</Label>
                <div className="relative">
                  <select
                    id="production_status"
                    {...register('production_status')}
                    className="flex h-10 w-full rounded-md border border-input bg-muted/60 px-3 py-2 pr-10 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer"
                  >
                    <option value="ready">Ready</option>
                    <option value="pre_order">Pre-Order</option>
                    <option value="discontinued">Discontinued</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="fill-current h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
                {errors.production_status && (
                  <p className="text-sm text-destructive">{errors.production_status.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="lead_time">Lead Time (days)</Label>
                <Input
                  id="lead_time"
                  type="number"
                  className="bg-muted/60"
                  {...register('lead_time', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={watchedValues.is_active}
                    onCheckedChange={(checked) => setValue('is_active', checked)}
                  />
                  <Label htmlFor="is_active" className="mb-0">
                    Active
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_featured"
                    checked={watchedValues.is_featured}
                    onCheckedChange={(checked) => setValue('is_featured', checked)}
                  />
                  <Label htmlFor="is_featured" className="mb-0">
                    Featured
                  </Label>
                </div>
              </div>
              <FormField
                id="warranty"
                label="Warranty (months)"
                error={!!errors.warranty}
                required
                showError={false}
              >
                <Input
                  id="warranty"
                  type="number"
                  className="bg-muted/60"
                  {...register('warranty', { valueAsNumber: true })}
                />
              </FormField>
            </CardContent>
          </Card>

          {id && (
            <ProductVariantsSection
              productId={id}
              variants={product.variants || []}
              onChanged={loadProduct}
            />
          )}

          {/* Technical Specifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Zap className="h-5 w-5 mr-2" />
                Technical Specifications
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="flow_rate">Flow Rate</Label>
                <Input
                  id="flow_rate"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('flow_rate', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="flow_rate_unit">Flow Rate Unit</Label>
                <div className="relative">
                  <select
                    id="flow_rate_unit"
                    value={watchedValues.flow_rate_unit ?? DEFAULT_FLOW_RATE_UNIT}
                    onChange={(event) =>
                      handleFlowRateUnitChange(event.target.value as FlowRateUnit)
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-muted/60 px-3 py-2 pr-10 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer"
                  >
                    <option value="m3_h">m³/h</option>
                    <option value="l_min">L/menit</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg
                      className="fill-current h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="head">Head (m)</Label>
                <Input
                  id="head"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('head', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="suction_lift_max">Max Suction Lift (m)</Label>
                <Input
                  id="suction_lift_max"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('suction_lift_max', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="pressure_max">Max Pressure (bar)</Label>
                <Input
                  id="pressure_max"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('pressure_max', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="fluid_temperature">Fluid Temperature (°C)</Label>
                <Input
                  id="fluid_temperature"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('fluid_temperature', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="ambient_temperature">Ambient Temperature (°C)</Label>
                <Input
                  id="ambient_temperature"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('ambient_temperature', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="motor_power">Motor Power (kW)</Label>
                <Input
                  id="motor_power"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('motor_power', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="voltage_phase">Voltage/Phase</Label>
                <Input
                  id="voltage_phase"
                  className="bg-muted/60"
                  {...register('voltage_phase', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="rpm">RPM</Label>
                <Input
                  id="rpm"
                  type="number"
                  className="bg-muted/60"
                  {...register('rpm', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="motor_efficiency">Motor Efficiency (%)</Label>
                <Input
                  id="motor_efficiency"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('motor_efficiency', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="isolation_class">Isolation Class</Label>
                <Input
                  id="isolation_class"
                  className="bg-muted/60"
                  {...register('isolation_class', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="ip_protection">IP Protection</Label>
                <Input
                  id="ip_protection"
                  className="bg-muted/60"
                  {...register('ip_protection', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Materials & Physical */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Wrench className="h-5 w-5 mr-2" />
                Materials & Physical Properties
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="housing_material">Housing Material</Label>
                <Input
                  id="housing_material"
                  className="bg-muted/60"
                  {...register('housing_material', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="impeller_material">Impeller Material</Label>
                <Input
                  id="impeller_material"
                  className="bg-muted/60"
                  {...register('impeller_material', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="shaft_material">Shaft Material</Label>
                <Input
                  id="shaft_material"
                  className="bg-muted/60"
                  {...register('shaft_material', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="mechanical_seal">Mechanical Seal</Label>
                <Input
                  id="mechanical_seal"
                  className="bg-muted/60"
                  {...register('mechanical_seal', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="inlet_outlet_connection">Inlet/Outlet Connection</Label>
                <Input
                  id="inlet_outlet_connection"
                  className="bg-muted/60"
                  {...register('inlet_outlet_connection', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="product_weight">Product Weight (kg)</Label>
                <Input
                  id="product_weight"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('product_weight', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="shipping_weight">Shipping Weight (kg)</Label>
                <Input
                  id="shipping_weight"
                  type="number"
                  step="0.01"
                  className="bg-muted/60"
                  {...register('shipping_weight', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="product_dimensions">Product Dimensions</Label>
                <Input
                  id="product_dimensions"
                  className="bg-muted/60"
                  {...register('product_dimensions', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? null : value,
                  })}
                />
              </div>
              <div>
                <Label htmlFor="length">Length (mm)</Label>
                <Input
                  id="length"
                  type="number"
                  className="bg-muted/60"
                  {...register('length', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="width">Width (mm)</Label>
                <Input
                  id="width"
                  type="number"
                  className="bg-muted/60"
                  {...register('width', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
              <div>
                <Label htmlFor="height">Height (mm)</Label>
                <Input
                  id="height"
                  type="number"
                  className="bg-muted/60"
                  {...register('height', {
                    setValueAs: (value) => {
                      if (value === '' || value === null || value === undefined) return null
                      const num = typeof value === 'string' ? parseFloat(value) : value
                      return isNaN(num) ? null : num
                    },
                  })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className="flex items-center justify-between">
            <Button type="button" variant="outline" onClick={handleReset} disabled={!hasChanges}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Changes
            </Button>
            <Button type="submit" disabled={!hasChanges || isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>

        {/* Right column: Product Images and Related */}
        <div className="space-y-6 self-start">
          {/* Images */}
          <Card>
            <CardHeader>
              <CardTitle>Product Images</CardTitle>
              <p className="text-sm text-muted-foreground">
                Maintain all images here. Parent product images stay separate from variant images,
                but everything is still managed from this same panel.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <ProductImages
                product={product}
                title="Parent Product Images"
                description="Dipakai sebagai fallback default ketika variant tidak punya image sendiri."
                imageLabel="Parent Images"
                embedded
                onImageUpload={handleImageUpload}
                onDeleteImage={handleDeleteImage}
                onSetPrimaryImage={handleSetPrimaryImage}
                isUploading={isUploadingImage}
              />

              {sortedVariants.length > 0 && (
                <div className="border-t pt-6 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      Variant Images
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Semua variant dikelompokkan di section ini. Variant default tetap diberi
                      penanda, tapi tidak dipisah jadi section tersendiri.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {sortedVariants.map((variant) => (
                      <div key={`variant-image-${variant.id}`} className="rounded-lg border p-4">
                        <ProductImages
                          product={{ id: variant.id, images: variant.images || [] }}
                          title={
                            variant.is_default ? `${variant.name} · Default Variant` : variant.name
                          }
                          description={`SKU ${variant.sku}`}
                          imageLabel="Variant Images"
                          embedded
                          onImageUpload={(files) => handleVariantImageUpload(variant.id, files)}
                          onDeleteImage={(imageId) => handleDeleteVariantImage(variant.id, imageId)}
                          onSetPrimaryImage={(imageId) =>
                            handleSetPrimaryVariantImage(variant.id, imageId)
                          }
                          isUploading={uploadingVariantId === variant.id}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Related Products</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-3">
                  {relatedProducts.map((related) => (
                    <Link key={related.id} to={`/products/${related.id}`} className="block">
                      <div className="border rounded-lg p-3 hover:bg-muted transition-colors">
                        <div className="font-medium truncate">{related.name}</div>
                        <div className="text-sm text-muted-foreground">{related.sku}</div>
                        <div className="text-sm font-semibold mt-1">
                          {formatPrice(related.price)}
                        </div>
                        <Badge variant="outline" className="mt-2 text-xs">
                          {related.category?.name || 'Uncategorized'}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProductDetailPage
